import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import PerksEditor from "@/components/plans/PerksEditor";
import ConfirmPriceDialog from "@/components/plans/ConfirmPriceDialog";

const period = (p) => (!p.interval ? "one-time" : p.interval === "year" ? "per year" : p.interval_count > 1 ? `every ${p.interval_count} months` : "per month");
const toForm = (p) => ({ name: p.product_name || "", description: p.description || "", price: String(p.amount), perks: p.perks || [] });
const input = "w-full min-h-[44px] px-3 bg-[#141414] border border-[#262626] text-white text-base sm:text-sm focus:outline-none focus:border-white";
const usd = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;

export default function PlanEditor({ api, plan, onSaved }) {
  const [form, setForm] = useState(() => toForm(plan));
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const id = plan.lookup_key;
  const cents = Math.round(parseFloat(form.price) * 100);
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(plan));
  const priceChanged = cents !== Math.round(plan.amount * 100);

  const save = async () => {
    setConfirm(false);
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/plans/${id}`, {
        name: form.name.trim(), description: form.description.trim(), amount_cents: cents, perks: form.perks.map((x) => x.trim()).filter(Boolean),
      });
      onSaved(data.plan);
      setForm(toForm(data.plan));
      toast.success(data.repriced ? `Saved. New price ${usd(data.plan.amount)} is live.` : "Saved. Changes are live.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save. Check the fields and try again.");
    } finally {
      setBusy(false);
    }
  };
  const submit = () => {
    if (!form.name.trim()) return toast.error("Plan name is required.");
    if (!(cents >= 100 && cents <= 10000000)) return toast.error("Price must be between $1 and $100,000.");
    return priceChanged ? setConfirm(true) : save();
  };

  return (
    <section className="brutal-border p-4 sm:p-5 flex flex-col gap-4" data-testid={`plan-editor-${id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="font-mono-label text-[10px] text-[#FFD600]">{id.toUpperCase().replace("CL_", "")} · {period(plan).toUpperCase()}</div>
        {plan.referral_eligible && <div className="font-mono-label text-[10px] text-muted-foreground">REFERRAL CODES APPLY</div>}
      </div>
      <label className="text-xs text-muted-foreground">Plan name
        <input value={form.name} maxLength={80} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`mt-1 ${input}`} data-testid={`plan-name-${id}`} />
      </label>
      <label className="text-xs text-muted-foreground">Description
        <textarea value={form.description} maxLength={400} rows={3} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className={`mt-1 py-2 resize-y ${input}`} data-testid={`plan-description-${id}`} />
      </label>
      <label className="text-xs text-muted-foreground">Price (USD, {period(plan)})
        <div className="relative mt-1 w-40">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
          <input type="number" min={1} step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
            className={`pl-7 ${input}`} data-testid={`plan-price-${id}`} />
        </div>
      </label>
      <PerksEditor id={id} perks={form.perks} onChange={(perks) => setForm({ ...form, perks })} />
      <div className="flex items-center gap-3 mt-1">
        <button onClick={submit} disabled={busy || !dirty} data-testid={`plan-save-${id}`}
          className="min-h-[44px] px-5 cta-primary font-mono-label text-[11px] inline-flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save changes
        </button>
        {dirty && !busy && (
          <button onClick={() => setForm(toForm(plan))} data-testid={`plan-reset-${id}`} className="text-xs text-muted-foreground hover:text-white">Undo edits</button>
        )}
      </div>
      <ConfirmPriceDialog open={confirm} onOpenChange={setConfirm} plan={plan} newPrice={cents / 100} onConfirm={save} />
    </section>
  );
}
